import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import AppsApi from '@/api/apps'
import { cloneDeep } from 'lodash'

interface HomeModuleFilters {
    brandName: string
    brandId: string
    platform: string
    status: string
    tab: string
}

@Module({ namespaced: true })
export default class HomeModule extends VuexModule {
    public apps: AppEntity[] = []
    public filters: HomeModuleFilters = { brandName: '', brandId: '', platform: '', status: '', tab: '' }
    public currentPage: number = 0
    public isLoading: boolean = false
    public isEndedResults: boolean = false

    @Mutation
    public setApps(apps: AppEntity[]) {
        this.apps = apps
    }

    @Mutation
    public setFilters(filters: HomeModuleFilters) {
        this.filters = filters
    }

    @Mutation
    public setCurrentPage(page: number) {
        this.currentPage = page
    }

    @Mutation
    public setLoading(loading: boolean) {
        this.isLoading = loading
    }

    @Mutation
    public setEndedResults(ended: boolean) {
        this.isEndedResults = ended
    }

    @Mutation
    public updateAppAtHomeModule(app: AppEntity) {
        const apps = this.apps
        const appIndex = this.apps.findIndex((element, index, array) => {
            return element.id === app.id
        })

        if (appIndex > -1) {
            apps[appIndex] = app
            this.apps = apps
        }
    }

    @Action
    public replaceApp(app: AppEntity) {
        const index = this.apps.findIndex((e) => app.id === e.id)
        const apps = this.apps
        apps[index] = app

        this.context.commit('setApps', [])
        this.context.commit('setApps', apps)
    }

    @Action
    public filterApps(filters: HomeModuleFilters) {
        this.context.commit('setFilters', filters)
        this.context.commit('setLoading', false)
        this.context.commit('setEndedResults', false)
        this.context.commit('setCurrentPage', 0)
        this.context.commit('setApps', [])
        this.context.dispatch('fetchApps')
    }

    @Action
    public async fetchApps(): Promise<boolean> {
        if (this.isLoading || this.isEndedResults) {
            return Promise.resolve(false)
        }

        let perPage = 20

        if (this.filters.status.includes('EXPIRED') || this.filters.status.includes('EXPIRING')) {
            perPage = 1000
        }

        let params = {
            limit: perPage,
            offset: this.currentPage * perPage,
            status: this.filters.status.trim(),
            platform: this.filters.platform,
        }

        const activeFilters = []
        if (this.filters.brandName.trim().length > 0) {
            const brandName = this.filters.brandName.trim()
            activeFilters.push(`Brands.name.like:%${brandName}%`)
        }
        if (this.filters.brandId.trim().length > 0) {
            const brandId = this.filters.brandId.trim()
            activeFilters.push(`Brands.id:${brandId}`)
        }

        if (activeFilters.length > 0) {
            params = Object.assign(params, { q: activeFilters.join(' AND ') })
        }

        this.context.commit('setLoading', true)

        const response = await AppsApi.getApps(params)
        if (response.status === 'cancelled') {
            return Promise.resolve(false)
        }

        if (response.status === 'success') {
            this.context.commit('setCurrentPage', this.currentPage + 1)

            if (response.data!.apps.length > 0) {
                this.context.commit('setApps', this.apps.concat(response.data!.apps))
            } else {
                this.context.commit('setEndedResults', true)
            }

        } else {
            alert(response.message)
            this.context.commit('setLoading', false)
            return Promise.resolve(false)
        }

        this.context.commit('setLoading', false)
        return Promise.resolve(true)
    }

    @Action
    public async getAppsWithErrors(): Promise<number> {
        const response = await AppsApi.getAppsWithErrors()

        if (response.status !== 'success' || response.data == null) {
            return Promise.resolve(0)
        }

        return Promise.resolve(response.data!.count)
    }
}
